import { useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api } from '../api/client';
import Mark from '../components/Mark';
import { useT } from '../i18n/i18n';
import { incompleteEmail } from '../auth/emailCheck';

export default function ForgotPasswordPage() {
  const t = useT();
  const loc = useLocation() as { state?: { email?: string } };
  const [email, setEmail] = useState(loc.state?.email ?? '');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (incompleteEmail(email)) { setError(t('This email address looks incomplete. Check the end, for example .com or .co.uk.')); return; }
    setBusy(true);
    try {
      await api<void>('/auth/forgot', { method: 'POST', body: { email: email.trim() } });
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('That could not be sent just now. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="forgot-heading" style={{ maxWidth: 460, margin: '0 auto' }}>
      <div className="card">
        <h2 id="forgot-heading">
          <Mark className="brand-mark" />
          {sent ? t('Check your email') : t('Forgotten your password?')}
        </h2>
        {sent ? (
          <>
            <p aria-live="polite">
              {t('If an EasyLearning account uses {email}, we have sent it a link to choose a new password.', { email: email.trim() })}
            </p>
            <p className="field-hint">
              {t('The link works for 60 minutes and can be used once. If it does not arrive within a few minutes, check your spam folder.')}
            </p>
            <div className="toolbar">
              <Link className="btn" to="/login">{t('Back to sign in')}</Link>
            </div>
          </>
        ) : (
          <form onSubmit={submit} className="spaced" noValidate>
            <p>{t('Enter the email address you use for EasyLearning. We will send you a link to choose a new password.')}</p>
            {error && <div className="error" role="alert">{error}</div>}
            <div className="field">
              <label htmlFor="forgot-email">{t('Email')}</label>
              <input
                id="forgot-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="toolbar">
              <button className="btn" type="submit" disabled={busy || !email.trim()}>
                {busy ? t('Working…') : t('Send me a link')}
              </button>
              <Link className="btn ghost" to="/login">{t('Back to sign in')}</Link>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
